import React, { useState } from 'react';
import { motion } from 'motion/react';
import { toast } from 'sonner';
import {
  Phone,
  MessageSquare,
  Shield,
  Mic,
  Brain,
  Zap,
  BarChart3,
  Check,
  ArrowRight,
  Star,
  Lock,
  FileText,
  Users,
  Eye,
  Server,
  Send,
  Globe,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PDFDownloadButton from '../components/PDFDownloadButton';
import '../styles/omniagent.css';

const features = [
  {
    category: 'Voice & Customer Conversations',
    icon: Mic,
    color: 'pink',
    items: [
      'Natural voice answering (human-like)',
      'Conversation memory (per call/session)',
      'Tone matching (professional, friendly, formal)',
      'Multilingual voice agents',
      'Real-time escalation to human agents',
      'Call summaries + suggested actions',
    ],
  },
  {
    category: 'Knowledge & Intelligence',
    icon: Brain,
    color: 'purple',
    items: [
      'Learns from company documents',
      'Uses internal database answers',
      'Role-specific knowledge (Sales vs Support vs HR)',
      'Continuous improvement training loop',
      'Knowledge confidence scoring',
    ],
  },
  {
    category: 'Automation & Actions',
    icon: Zap,
    color: 'orange',
    items: [
      'Appointment booking',
      'Ticket/case creation',
      'CRM updates',
      'Order status lookup',
      'Identity verification flow',
      'Automated follow-ups via email/SMS',
    ],
  },
  {
    category: 'Security & Compliance',
    icon: Shield,
    color: 'green',
    items: [
      'Private knowledge environment',
      'No training on public models',
      'GDPR-ready by design',
      'Access control & permissions',
      'Audit logs & reporting',
    ],
  },
  {
    category: 'Management & Analytics',
    icon: BarChart3,
    color: 'blue',
    items: [
      'Performance dashboard',
      'Insights: top customer issues',
      'Agent effectiveness score',
      'Cost savings estimator',
      'Continuous improvement suggestions',
    ],
  },
];

const packages = [
  {
    name: 'Essentials',
    tagline: 'OmniAgent Starter',
    description: 'Perfect for small companies, single department, pilot projects',
    setupTime: '2-4 weeks',
    bestFor: 'Customer support basics, simple call automation, lead capture',
    features: [
      '1 AI voice agent (calls)',
      '1 AI chat agent (website/app)',
      'Knowledge ingestion (documents + FAQ)',
      'Basic call routing & escalation',
      'Basic actions (cases, booking, CRM notes)',
      'Multilingual support (up to 3 languages)',
      'Monthly analytics report',
    ],
    color: 'blue',
    popular: false,
  },
  {
    name: 'Growth',
    tagline: 'OmniAgent Professional',
    description: 'Best for scaling support + sales + multiple teams',
    setupTime: '4-8 weeks',
    bestFor: 'Customer centers, sales departments, companies with 5-100 agents',
    features: [
      'Everything in Essentials +',
      'Multiple AI agents (support + sales + internal)',
      'CRM, ticketing, calendar integrations',
      'Smart routing & identity verification',
      'Sentiment detection',
      'Booking + confirmations + follow-ups',
      'Full multilingual (10+ languages)',
      'Custom tone & brand alignment',
      'Real-time dashboard',
      'Continuous training improvements',
    ],
    color: 'orange',
    popular: true,
  },
  {
    name: 'Enterprise',
    tagline: 'OmniAgent Secure Enterprise',
    description: 'Full enterprise-level AI agent system, 24/7 automation',
    setupTime: '6-12 weeks',
    bestFor: 'Large organizations, regulated industries, global operations',
    features: [
      'Everything in Growth +',
      'Dedicated environment (private cloud/on-prem)',
      'SSO / IAM integration',
      'Role-based access control',
      'Audit & compliance logs',
      'ERP, internal apps, custom API integrations',
      'Custom workflows per department',
      'Industry templates (Finance/Health/Gov)',
      'Human-in-the-loop controls',
      'SLA + 24/7 monitoring',
      'Quarterly optimization + roadmap',
    ],
    color: 'purple',
    popular: false,
  },
];

const securityFeatures = [
  { icon: Shield, title: 'Private Knowledge Environment', description: 'Your company data is handled privately' },
  { icon: Lock, title: 'No Data Leakage', description: 'Knowledge hosted in secured environment' },
  { icon: FileText, title: 'GDPR-Ready by Design', description: 'Compliance with industry regulations' },
  { icon: Users, title: 'Access Control', description: 'Role-based permissions & team management' },
  { icon: Eye, title: 'Audit Logs', description: 'Complete audit trails & reporting' },
  { icon: Server, title: 'Flexible Deployment', description: 'Private cloud, on-prem, or hybrid' },
];

const OmniAgentPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Open email client with form data
    const subject = encodeURIComponent(`OmniAgent Demo Request from ${formData.name}`);
    const body = encodeURIComponent(`Name: ${formData.name}\nEmail: ${formData.email}\nCompany: ${formData.company}\n\nMessage:\n${formData.message}`);
    window.location.href = `mailto:sales@voxitconsulting.media?subject=${subject}&body=${body}`;
    
    toast.success('Opening email client...');
    setFormData({ name: '', email: '', company: '', message: '' });
    setIsSubmitting(false);
  };

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <div className="omni-page" id="omniagent-content">
      <Header />
      
      {/* Hero Section */}
      <section className="omni-hero">
        <div className="container-custom">
          <div className="omni-hero-content">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="omni-badge"
            >
              <span className="pulse-dot" />
              Now Available in 10+ Languages
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
            >
              Your Secure AI Agent for Calls, Sales, and Support
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="omni-subtitle"
            >
              Trained on your company knowledge. Answer phone calls like a real agent.
              Resolve issues. Take actions. All without exposing your data to public AI.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="omni-hero-buttons"
            >
              <a href="mailto:sales@voxitconsulting.media?subject=Request Demo - VoxIT OmniAgent" className="btn-omni-primary">
                Request a Demo <ArrowRight size={16} />
              </a>
              <a href="#features" className="btn-omni-secondary">
                Explore Features
              </a>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="omni-preview-cards"
            >
              <div className="preview-card pink">
                <div className="preview-icon"><Phone size={24} /></div>
                <span className="preview-title">Voice AI Agent</span>
                <span className="preview-desc">Answer calls naturally</span>
              </div>
              <div className="preview-card blue">
                <div className="preview-icon"><MessageSquare size={24} /></div>
                <span className="preview-title">Chat Support</span>
                <span className="preview-desc">Instant responses</span>
              </div>
              <div className="preview-card green">
                <div className="preview-icon"><Shield size={24} /></div>
                <span className="preview-title">Enterprise Security</span>
                <span className="preview-desc">Your data stays private</span>
              </div>
            </motion.div>
          </div>
        </div>
        
        <div className="omni-hero-bg">
          <div className="blob-1" />
          <div className="blob-2" />
        </div>
      </section>
      
      {/* Features Section */}
      <section className="omni-features" id="features">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="section-header-omni"
          >
            <span className="section-tag-omni">Core Features</span>
            <h2>Everything You Need for Intelligent Automation</h2>
            <p>Comprehensive AI capabilities designed for enterprise-grade customer interactions</p>
          </motion.div>
          
          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div
                key={feature.category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className={`feature-card ${feature.color}`}
              >
                <div className="feature-icon">
                  <feature.icon size={20} />
                </div>
                <h3>{feature.category}</h3>
                <ul>
                  {feature.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="differentiator-box"
          >
            <h3>Key Differentiator</h3>
            <p>
              Most bots = "text only" — <strong>Voxit OmniAgent = Voice + Chat + Real actions + Secure company environment</strong>
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* Pricing Section */}
      <section className="omni-pricing" id="pricing">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="section-header-omni"
          >
            <span className="section-tag-omni">Pricing Packages</span>
            <h2>Choose Your Perfect Plan</h2>
            <p>Scalable solutions from pilot projects to enterprise deployments</p>
          </motion.div>
          
          <div className="pricing-grid">
            {packages.map((pkg, index) => (
              <motion.div
                key={pkg.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className={`pricing-card ${pkg.popular ? 'popular' : ''}`}
              >
                {pkg.popular && (
                  <div className="popular-badge">
                    <Star size={12} fill="currentColor" /> Most Popular
                  </div>
                )}
                
                <div className={`pricing-header ${pkg.color}`}>
                  <span className="pricing-tagline">{pkg.tagline}</span>
                  <h3>{pkg.name}</h3>
                </div>
                
                <div className="pricing-body">
                  <p className="pricing-desc">{pkg.description}</p>
                  
                  <div className="pricing-meta">
                    <span>Setup Time:</span>
                    <strong>{pkg.setupTime}</strong>
                  </div>
                  
                  <div className="pricing-best-for">
                    <span>Best For:</span>
                    <p>{pkg.bestFor}</p>
                  </div>
                  
                  <ul className="pricing-features">
                    {pkg.features.map((feature, i) => (
                      <li key={i}>
                        <Check size={16} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <a
                    href="#contact"
                    className={pkg.popular ? 'btn-omni-primary w-full' : 'btn-omni-secondary w-full'}
                  >
                    Get Started <ArrowRight size={16} />
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Security Section */}
      <section className="omni-security">
        <div className="container-custom">
          <div className="security-content">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="security-text"
            >
              <span className="section-tag-omni">Security & Privacy</span>
              <h2>Enterprise-Grade Security</h2>
              <p>
                Unlike public AI tools like ChatGPT, Voxit OmniAgent is designed for
                organizations that require high confidentiality. Your business-sensitive
                data never leaves your controlled environment.
              </p>
              
              <div className="security-promise">
                <h4>Our Security Promise</h4>
                <p>
                  "A secure AI agent that answers your company's calls, learns your
                  knowledge, and takes actions—without exposing your data to public AI."
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="security-grid"
            >
              {securityFeatures.map((feature, index) => (
                <div key={feature.title} className="security-item">
                  <feature.icon size={20} />
                  <h5>{feature.title}</h5>
                  <p>{feature.description}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Contact Section */}
      <section className="omni-contact exclude-from-pdf" id="contact">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="section-header-omni"
          >
            <span className="section-tag-omni">Get In Touch</span>
            <h2>Ready to Transform Your Operations?</h2>
            <p>Request a demo and see how Voxit OmniAgent can revolutionize your customer interactions</p>
          </motion.div>
          
          <div className="contact-grid">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="contact-form-card"
            >
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Work Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@company.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Company Name</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Acme Inc."
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Tell us about your needs</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    placeholder="What challenges are you looking to solve?"
                  />
                </div>
                <button type="submit" className="btn-omni-primary w-full" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : <><Send size={16} /> Request Demo</>}
                </button>
              </form>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="contact-info"
            >
              <div className="info-card orange">
                <h4>Why Choose Voxit OmniAgent?</h4>
                <ul>
                  <li><ArrowRight size={14} /> Voice + Chat + Real actions in one platform</li>
                  <li><ArrowRight size={14} /> Enterprise-grade security by design</li>
                  <li><ArrowRight size={14} /> Custom-trained on your company knowledge</li>
                  <li><ArrowRight size={14} /> Multilingual support (10+ languages)</li>
                  <li><ArrowRight size={14} /> Rapid deployment (2-12 weeks)</li>
                </ul>
              </div>
              
              <div className="info-card blue">
                <h4>Global Reach</h4>
                <p>
                  Voxit OmniAgent is perfectly positioned for global markets with
                  multilingual capabilities and compliance with international regulations.
                </p>
                <div className="region-tags">
                  {['Africa', 'Europe', 'Americas', 'Asia', 'Middle East'].map((region) => (
                    <span key={region} className="region-tag">
                      <Globe size={12} /> {region}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      <Footer />
      <PDFDownloadButton 
        contentId="omniagent-content" 
        fileName="VoxIT-OmniAgent" 
        buttonText="Download PDF"
      />
    </div>
  );
};

export default OmniAgentPage;
